import { type Ref, useEffectEvent, useLayoutEffect } from "react";
import ShopWindowHeader from "@/components/shopwindow/ShopWindowHeader";
import ArticleImageSwiper from "@/components/shopwindow/ArticleImageSwiper";
import ShopWindowCtaFooter from "@/components/shopwindow/ShopWindowCtaFooter";
import ShopWindowFavoritesFooter from "@/components/shopwindow/ShopWindowFavoritesFooter";
import { shopWindowTypes, eventNames } from "@/modules/Constants";
import type ShopWindowConfig from "@/plugin/ShopWindowConfig";
import type { ExperienceState } from "@/types/state";

const REGION = "Product Shop Window";

interface CtaLink {
  label?: { tap?: { title?: string } };
}

type ProductShopWindowProps = {
  experienceState: ExperienceState;
  config: ShopWindowConfig;
  hasHeader?: boolean;
  hasExitButton?: boolean;
  nodeRef?: Ref<HTMLDivElement>;
  onApplicationEvent: (event: Record<string, unknown>) => void;
};

export default function ProductShopWindow({
  experienceState,
  config,
  hasHeader,
  hasExitButton,
  nodeRef,
  onApplicationEvent,
}: ProductShopWindowProps) {
  const onDidMount = useEffectEvent(() => {
    onApplicationEvent({
      region: REGION,
      name: eventNames.SHOP_WINDOW_DID_MOUNT,
      type: shopWindowTypes.PRODUCT,
    });
  });
  // componentDidMount: layout phase, after the children's mount effects.
  useLayoutEffect(() => {
    onDidMount();
  }, []);

  const onExit = () => {
    if (onApplicationEvent) {
      onApplicationEvent({
        name: eventNames.SHOP_WINDOW_EXIT,
        region: REGION,
        shopWindowConfig: config,
      });
    }
  };

  const { experienceData, applicationData } = experienceState;
  const ctaLink = config.links ? config.links.cta : null;
  const showHintTap = config.data.showHintTap;

  const header = hasHeader ? (
    <ShopWindowHeader
      src={experienceData.getLogoImageUrl()}
      hasExitButton={hasExitButton}
      onExit={onExit}
    />
  ) : null;

  let hintTap = null;

  if (showHintTap && applicationData.isPortrait()) {
    const clusterIndex = config.clusterArticleIndexAt(
      experienceData,
      config.index,
    );
    const label = clusterIndex.cta ? (clusterIndex.cta as CtaLink).label : null;
    const tapText = label?.tap ? label.tap.title : " ";
    hintTap = (
      <div className="hint-tap tw:[.portrait_.product-shop-window_&]:order-2 tw:[.portrait_.shop-window_&]:[line-height:1]">
        <span>{tapText}</span>
      </div>
    );
  }

  let footer = null;
  if (config.data.isFavorites) {
    footer = (
      <ShopWindowFavoritesFooter
        experienceState={experienceState}
        config={config}
        onApplicationEvent={onApplicationEvent}
      />
    );
  } else if (ctaLink) {
    footer = (
      <ShopWindowCtaFooter
        experienceState={experienceState}
        config={config}
        onApplicationEvent={onApplicationEvent}
      />
    );
  }

  return (
    <div
      className="product-shop-window tw:absolute tw:inset-0 tw:flex tw:flex-col tw:items-stretch tw:bg-[#fff] tw:[.portrait_&]:p-0"
      ref={nodeRef}
    >
      {header}
      {hintTap}
      <ArticleImageSwiper
        experienceState={experienceState}
        config={config}
        onApplicationEvent={onApplicationEvent}
      />
      {footer}
    </div>
  );
}
